import styled from 'styled-components';

// Sizing follows the app's 10px rem base and theme colours so the page matches
// the JJ content pages (e.g. /content/supply-us).

export const Intro = styled.p`
  font-size: 1.6rem;
  line-height: 2.6rem;
  color: ${p => p.theme.quaternaryColor};
  max-width: 70rem;
  margin-bottom: 2rem;
`;

export const Benefits = styled.ul`
  margin: 0 0 3rem 2rem;
  padding: 0;
  max-width: 70rem;

  li {
    font-size: 1.6rem;
    line-height: 2.8rem;
    color: ${p => p.theme.quaternaryColor};
  }
`;

// Eligibility notice shown above the application form (£2,000 minimum monthly
// spend guideline). Styled to match the page's panels for consistency.
export const Eligibility = styled.p`
  max-width: 60rem;
  margin-bottom: 2.5rem;
  padding: 2rem;
  background: ${p => p.theme.tertiaryBgColor};
  border: 1px solid ${p => p.theme.borderColor};
  border-radius: 0.6rem;
  font-size: 1.5rem;
  line-height: 2.4rem;
  color: ${p => p.theme.quaternaryColor};
`;

export const Panel = styled.div`
  max-width: 60rem;
  background: ${p => p.theme.tertiaryBgColor};
  border: 1px solid ${p => p.theme.borderColor};
  border-radius: 0.6rem;
  padding: 3rem;
`;

export const Field = styled.div`
  margin-bottom: 2rem;
`;

export const Label = styled.label`
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: ${p => p.theme.quaternaryColor};
  margin-bottom: 0.8rem;
`;

export const Hint = styled.p`
  font-size: 1.3rem;
  line-height: 1.8rem;
  color: ${p => p.theme.textColor};
  margin: -0.4rem 0 0.8rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 1.2rem;
  border: 1px solid
    ${p => (p.$hasError ? p.theme.tertiaryColor : p.theme.borderColor)};
  border-radius: 0.4rem;
  font-size: 1.6rem;
  color: ${p => p.theme.quaternaryColor};

  &:focus {
    outline: none;
    border-color: ${p =>
      p.$hasError ? p.theme.tertiaryColor : p.theme.secondaryColor};
  }
`;

// Confirmation panel shown beneath the Companies House number field once a
// company has been looked up, so the user can check they picked the right one.
export const LookupResult = styled.div`
  margin-top: 1rem;
  padding: 1.4rem 1.6rem;
  border: 1px solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  background: ${p => p.theme.secondaryBgColor};
`;

export const CompanyName = styled.p`
  font-size: 1.6rem;
  font-weight: bold;
  color: ${p => p.theme.quaternaryColor};
  margin: 0 0 0.4rem;
`;

export const CompanyAddress = styled.p`
  font-size: 1.3rem;
  line-height: 1.9rem;
  color: ${p => p.theme.textColor};
  margin: 0.4rem 0 0;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: capitalize;
  padding: 0.2rem 0.8rem;
  border-radius: 1.2rem;
  color: ${p => (p.active ? '#1b7a3d' : '#a12622')};
  background: ${p => (p.active ? '#eafaf0' : '#fdecea')};
`;

export const LookupHint = styled.p`
  margin-top: 1rem;
  font-size: 1.3rem;
  line-height: 1.9rem;
  color: ${p =>
    p.variant === 'error' ? p.theme.tertiaryColor : p.theme.textColor};
`;

export const ConsentRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.4rem;
  font-size: 1.4rem;
  line-height: 2rem;
  color: ${p => p.theme.textColor};
  cursor: pointer;

  input {
    margin-top: 0.3rem;
    flex-shrink: 0;
    width: 1.6rem;
    height: 1.6rem;
  }

  a {
    color: ${p => p.theme.primaryColor};
    text-decoration: underline;
  }
`;

export const Disclaimer = styled.p`
  font-size: 1.3rem;
  line-height: 1.9rem;
  color: ${p => p.theme.textColor};
  margin: 2rem 0;
`;

export const Button = styled.button`
  padding: 1.2rem 2.8rem;
  background-color: ${p => p.theme.secondaryColor};
  color: ${p => p.theme.primaryTextColor};
  border: none;
  border-radius: 0.4rem;
  font-weight: bold;
  font-size: 1.6rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${p => p.theme.secondaryColorHover};
  }

  &:disabled {
    background-color: ${p => p.theme.disabledColor};
    cursor: not-allowed;
  }
`;

export const Message = styled.div`
  max-width: 70rem;
  border-radius: 0.6rem;
  padding: 2.4rem;
  font-size: 1.5rem;
  line-height: 2.4rem;
  white-space: pre-line;
  border: 1px solid;
  color: ${p => p.theme.quaternaryColor};
  background: ${p =>
    p.variant === 'declined' || p.variant === 'error'
      ? '#fdecea'
      : p.variant === 'referred'
      ? '#eafaf0'
      : '#fff8e1'};
  border-color: ${p =>
    p.variant === 'declined' || p.variant === 'error'
      ? p.theme.tertiaryColor
      : p.variant === 'referred'
      ? p.theme.secondaryColor
      : '#ffc107'};
`;

export const ErrorText = styled.span`
  color: ${p => p.theme.tertiaryColor};
  font-size: 1.2rem;
  margin-top: 1rem;
  display: block;
`;

export const Links = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1.5rem;

  a {
    color: ${p => p.theme.primaryColor};
    font-weight: bold;
    font-size: 1.5rem;
    text-decoration: none;
  }
`;
